<template>
  <section class="dashboard-section">
    <transition name="signal-popup">
      <div
        v-if="signalAlert"
        class="signal-popup"
        :class="signalAlert.side === 'sell' ? 'signal-sell' : 'signal-buy'"
        role="alert"
      >
        <button type="button" class="signal-close" aria-label="Fechar sinal" @click="closeSignal">×</button>
        <div class="signal-side">{{ signalAlert.side === 'sell' ? 'VENDA' : 'COMPRA' }}</div>
        <div class="signal-symbol">{{ signalAlert.symbol || 'Ativo' }}</div>
        <div v-if="signalAlert.price != null" class="signal-price">{{ formatSignalPrice(signalAlert.price) }}</div>
        <div class="signal-meta">
          <span v-if="signalAlert.timeframe">Tempo: {{ signalAlert.timeframe }}</span>
          <span v-if="signalAlert.kind">{{ signalAlert.kind }}</span>
        </div>
      </div>
    </transition>

    <i v-if="loading" class="fas fa-spinner loading-big"></i>

    <div v-else-if="error" class="empty-state">
      {{ error }}
    </div>

    <div v-else class="dashboard-frame-wrap">
      <div v-if="frameHint" class="frame-hint">{{ frameHint }}</div>
      <iframe
        :src="iframeSrc"
        :style="frameStyle"
        title="Swiss Money Dashboard"
        class="dashboard-frame"
        allow="fullscreen"
        @load="onFrameLoad"
      />
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import logger from '../../logger'
import { getToken } from '../../util'
import { getAccess, logAccess } from '../api/dash'

const KICK_MESSAGE = 'Dashboard aberto em outro dispositivo. Esta sessão foi encerrada.'

export default {
  name: 'Dashboard',
  data () {
    return {
      loading: true,
      error: null,
      iframeSrc: null,
      frameHint: null,
      frameLoadTimer: null,
      signalTimer: null,
      signalAlert: null,
      socketBound: false,
      joined: false,
      tabId: null,
      scale: 0.9
    }
  },
  computed: {
    ...mapState(['user']),
    frameStyle () {
      const s = this.scale
      const pct = `${(100 / s).toFixed(4)}%`
      return {
        width: pct,
        height: pct,
        transform: `scale(${s})`,
        transformOrigin: 'top left'
      }
    }
  },
  async mounted () {
    if (!this.user) {
      this.$router.push('/')
      return
    }

    this.tabId = this.getOrCreateTabId()
    document.body.style.backgroundColor = '#0b0e11'
    await this.loadAccess()
  },
  beforeDestroy () {
    this.teardownSocket()
    this.clearTimers()
  },
  methods: {
    getOrCreateTabId () {
      const key = 'dash_tab_id'
      try {
        let id = window.sessionStorage.getItem(key)
        if (!id) {
          id = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
          window.sessionStorage.setItem(key, id)
        }
        return id
      } catch (e) {
        return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
      }
    },
    apiBase () {
      const base = process.env.VUE_APP_API
      if (!base || base === 'undefined') return null
      return String(base).replace(/\/$/, '')
    },
    clearTimers () {
      if (this.frameLoadTimer) {
        clearTimeout(this.frameLoadTimer)
        this.frameLoadTimer = null
      }
      if (this.signalTimer) {
        clearTimeout(this.signalTimer)
        this.signalTimer = null
      }
    },
    onDashSignal (signal) {
      if (!signal || (signal.side !== 'buy' && signal.side !== 'sell')) return
      this.signalAlert = signal
      if (this.signalTimer) clearTimeout(this.signalTimer)
      this.signalTimer = setTimeout(() => {
        this.signalAlert = null
        this.signalTimer = null
      }, 15000)
    },
    closeSignal () {
      this.signalAlert = null
      if (this.signalTimer) {
        clearTimeout(this.signalTimer)
        this.signalTimer = null
      }
    },
    formatSignalPrice (price) {
      const value = Number(price)
      if (Number.isNaN(value)) return price
      return value.toLocaleString('pt-BR', { maximumFractionDigits: 8 })
    },
    kickSession (message) {
      this.clearTimers()
      this.teardownSocket()
      this.iframeSrc = null
      this.joined = false
      this.error = message || KICK_MESSAGE
    },
    onDashKicked (payload) {
      this.kickSession((payload && payload.message) || KICK_MESSAGE)
    },
    async onDashJoined (payload) {
      this.joined = true
      if (payload && payload.reconnected) return
      try {
        if (payload && payload.kickedOther) {
          await logAccess({ tabId: this.tabId, event: 'kick' })
        }
        await logAccess({ tabId: this.tabId, event: 'join' })
      } catch (e) {
        logger.error(e, this)
      }
    },
    onDashError (payload) {
      this.kickSession((payload && payload.error) || 'Não foi possível abrir a sessão do Dashboard.')
    },
    onSocketConnect () {
      if (this.iframeSrc && !this.error) {
        this.emitJoin()
      }
    },
    emitJoin () {
      const token = getToken()
      if (!token || !this.$socket) return
      this.$socket.emit('dash:join', { token, tabId: this.tabId })
    },
    bindSocket () {
      if (!this.$socket || this.socketBound) return
      this.$socket.on('dash:kicked', this.onDashKicked)
      this.$socket.on('dash:joined', this.onDashJoined)
      this.$socket.on('dash:error', this.onDashError)
      this.$socket.on('dash:signal', this.onDashSignal)
      this.$socket.on('connect', this.onSocketConnect)
      this.socketBound = true
    },
    teardownSocket () {
      if (!this.$socket || !this.socketBound) return
      try {
        this.$socket.emit('dash:leave')
        this.$socket.off('dash:kicked', this.onDashKicked)
        this.$socket.off('dash:joined', this.onDashJoined)
        this.$socket.off('dash:error', this.onDashError)
        this.$socket.off('dash:signal', this.onDashSignal)
        this.$socket.off('connect', this.onSocketConnect)
      } catch (e) {
        logger.error(e, this)
      }
      this.socketBound = false
    },
    async loadAccess () {
      try {
        this.loading = true
        this.error = null
        this.frameHint = null
        this.clearTimers()

        const api = this.apiBase()
        if (!api) {
          this.error = 'VUE_APP_API não configurada. Reinicie o frontend (npm run dev).'
          return
        }

        const accessData = await getAccess()
        if (!accessData || !accessData.access) {
          this.error = 'Você precisa de uma assinatura Dash ativa para acessar o Dashboard.'
          return
        }

        const token = getToken()
        if (!token) {
          this.error = 'Sessão expirada. Faça login novamente.'
          return
        }

        this.bindSocket()
        this.emitJoin()
        this.iframeSrc = `${api}/dash/app/?jwt=${encodeURIComponent(token)}`

        this.frameHint = 'Carregando...'
        this.frameLoadTimer = setTimeout(() => {
          this.frameHint = null
        }, 5000)
      } catch (e) {
        logger.error(e, this)
        this.error = 'Não foi possível carregar o Dashboard. Tente novamente.'
      } finally {
        this.loading = false
      }
    },
    onFrameLoad () {
      if (this.frameLoadTimer) {
        clearTimeout(this.frameLoadTimer)
        this.frameLoadTimer = null
      }
      this.frameHint = null
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../_asset/scss/_variables";

.dashboard-section {
  display: flex;
  flex-direction: column;
  margin: -$padding-mobile;
  width: calc(100% + #{$padding-mobile} * 2);
  min-height: calc(100vh - #{$menu-height});
  height: calc(100vh - #{$menu-height});
}

.empty-state {
  margin: $padding-mobile;
  text-align: center;
  padding: 48px 16px;
  color: $lp-text-2;
  border: 1px dashed $lp-border;
  border-radius: $lp-radius;
  background: $lp-panel;
}

.dashboard-frame-wrap {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #0b0e11;
}

.frame-hint {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 1;
  padding: 10px 14px;
  font-size: 0.85em;
  color: $lp-text-2;
  background: rgba(11, 14, 17, 0.92);
  border-bottom: 1px solid $lp-border;
}

.dashboard-frame {
  display: block;
  border: 0;
  background: #0b0e11;
}

.signal-popup {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 10000;
  width: #{"min(340px, calc(100vw - 32px))"};
  padding: 20px 48px 18px 20px;
  color: #fff;
  border: 1px solid;
  border-radius: 14px;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.48);
}

.signal-buy {
  background: linear-gradient(135deg, #123f2c, #0b241b);
  border-color: #35d07f;
}

.signal-sell {
  background: linear-gradient(135deg, #501f27, #2b1117);
  border-color: #ff6675;
}

.signal-close {
  position: absolute;
  top: 8px;
  right: 12px;
  padding: 4px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 25px;
  line-height: 1;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.signal-side {
  margin-bottom: 3px;
  font-size: 0.8em;
  font-weight: 800;
  letter-spacing: 0.14em;
}

.signal-symbol {
  font-size: 1.55em;
  font-weight: 700;
}

.signal-price {
  margin-top: 4px;
  font-size: 1.2em;
  font-weight: 600;
}

.signal-meta {
  display: flex;
  gap: 12px;
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.82em;
}

.signal-popup-enter-active,
.signal-popup-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.signal-popup-enter,
.signal-popup-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media screen and (min-width: $desktop-width) {
  .dashboard-section {
    margin: -20px -25px;
    width: calc(100% + 50px);
    min-height: 100vh;
    height: 100vh;
  }
}
</style>
